export type Envelope<T> = {
  requestId: string;
  timestamp: string;
  data: T;
};

export type ErrorResponse = {
  requestId: string;
  timestamp: string;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

export type ApiOk<T> = {
  ok: true;
  status: number;
  headers: Headers;
  body: Envelope<T>;
};

export type ApiErr = {
  ok: false;
  status: number;
  headers: Headers;
  body: ErrorResponse | null;
  rawText?: string;
};

export type ApiResult<T> = ApiOk<T> | ApiErr;

async function safeReadText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

function safeJsonParse(text: string): any | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * 统一 API 调用入口：
 * - 永远调用相对路径（/v1/..），从而在：
 *   - 本地：由 Vite dev server proxy 转发
 *   - 线上：由 Cloudflare Pages Functions 转发
 */
export async function api<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "accept": "application/json",
      ...(init?.headers ?? {}),
    },
  });

  const text = await safeReadText(res);
  const json = safeJsonParse(text);

  if (res.ok) {
    return {
      ok: true,
      status: res.status,
      headers: res.headers,
      body: json as Envelope<T>,
    };
  }

  return {
    ok: false,
    status: res.status,
    headers: res.headers,
    body: (json as ErrorResponse) ?? null,
    rawText: text,
  };
}

export async function apiGet<T>(path: string): Promise<ApiResult<T>> {
  return api<T>(path, { method: "GET" });
}

export async function apiPost<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return api<T>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}
